/* Landing V2 - Diagnosis as Product
 *
 * Direct port of the design handoff (landing-v2.jsx). Inline styles mirror the
 * prototype 1:1 for fidelity. Responsive overrides live in landing-v2.scss.
 *
 * Nav, Footer, BrandLogo, Eyebrow and shared tokens come from EditorialShell
 * so legal/pricing pages can share the same chrome.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    EditorialNav,
    EditorialFooter,
    Eyebrow,
    QUIZ_PATH,
    mono,
    serif,
    hairline,
    hair2,
    ivory,
    bodyText,
    muted,
    subtle,
} from './landing/EditorialShell';
import { useT } from '../i18n/useT';
import { DimensionId, DIMENSION_ORDER, DIMENSION_COLORS } from '../utils/dimensionMeta';

import '../assets/scss/landing-v2.scss';

// The landing's two accent colours, sourced from the canonical track palette so
// the marketing page never drifts from the product's own colour scale. Same hex
// values the page always used — `self` is the gold, `affect` the ember.
const ACCENT = { gold: DIMENSION_COLORS.self, ember: DIMENSION_COLORS.affect };

// ─── Sections ─────────────────────────────────────────────────────────

// 4-tier score color (mirrors lp-shared / dashboard tokens).
function scoreToColor(s: number): string {
    if (s < 40) return '#ef4444'; // red
    if (s < 65) return '#f59e0b'; // amber
    if (s < 85) return '#84cc16'; // lime

    return '#10b981'; // emerald
}

/** Returns the i18n key suffix for the zone label (resolved via `landing.zone.*`). */
function scoreToZoneKey(s: number): string {
    if (s < 40) return 'recovery';
    if (s < 65) return 'functional';
    if (s < 85) return 'optimized';

    return 'peak';
}

/** Returns the i18n key suffix for the journey caption (resolved via `landing.caption.*`). */
function scoreToCaptionKey(s: number): string {
    if (s < 30) return 'chaos';
    if (s < 50) return 'recovery';
    if (s < 70) return 'clarity';
    if (s < 90) return 'peak';

    return 'you';
}

type AnimPhase = 'climb' | 'peak' | 'reset' | 'idle';

const SCORE_START = 20;
const SCORE_PEAK = 92;
const CLIMB_MS = 9000;
const PEAK_MS = 1600;
const RESET_MS = 500;

const Hero: React.FC = () => {
    const navigate = useNavigate();
    const t = useT();
    const size = 360;
    const stroke = 3;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;

    // Animation state: score climbs SCORE_START -> SCORE_PEAK with eased timing,
    // pauses, snaps back, then idles. Click on the orb restarts the journey.
    const [score, setScore] = useState(SCORE_START);
    const [phase, setPhase] = useState<AnimPhase>('climb');
    const [restartKey, setRestartKey] = useState(0);

    useEffect(() => {
        if (phase === 'idle') return undefined;

        let raf = 0;
        let startMs = 0;

        const tick = (now: number) => {
            if (!startMs) startMs = now;
            const elapsed = now - startMs;

            if (phase === 'climb') {
                const t = Math.min(elapsed / CLIMB_MS, 1);
                // easeInOutQuad - slow start + slow finish, brisk middle
                const eased = t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2;

                setScore(SCORE_START + (SCORE_PEAK - SCORE_START) * eased);
                if (t >= 1) {
                    setPhase('peak');

                    return;
                }
            } else if (phase === 'peak') {
                if (elapsed >= PEAK_MS) {
                    setPhase('reset');

                    return;
                }
            } else if (phase === 'reset') {
                const t = Math.min(elapsed / RESET_MS, 1);

                setScore(SCORE_PEAK - (SCORE_PEAK - SCORE_START) * t * t);
                if (t >= 1) {
                    setScore(SCORE_START);
                    setPhase('idle');

                    return;
                }
            }

            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);

        return () => cancelAnimationFrame(raf);
    }, [phase, restartKey]);

    const handleRestart = () => {
        setScore(SCORE_START);
        setPhase('climb');
        setRestartKey((k) => k + 1);
    };

    const orbColor = scoreToColor(score);
    const zoneName = t(`landing.zone.${scoreToZoneKey(score)}`);
    const caption = t(`landing.caption.${scoreToCaptionKey(score)}`);
    // Highlight the CTA at peak AND after the journey settles into idle, so it
    // stays visible until the user replays the animation.
    const isPeak = phase === 'peak' || phase === 'idle';
    const scoreInt = Math.round(score);

    // Canvas-driven 3D particles, same technique as the dashboard HeroOrb.
    // The canvas runs its own rAF loop and reads the latest orb color from a
    // ref, so the particle field re-tints smoothly as the score climbs through
    // tiers without restarting the animation.
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const orbColorRef = useRef(orbColor);

    orbColorRef.current = orbColor;

    useEffect(() => {
        const cnv = canvasRef.current;

        if (!cnv) return undefined;
        const ctx = cnv.getContext('2d');

        if (!ctx) return undefined;

        const innerSize = size - 24;
        const dpr = window.devicePixelRatio || 1;

        cnv.width = innerSize * dpr;
        cnv.height = innerSize * dpr;
        cnv.style.width = `${innerSize}px`;
        cnv.style.height = `${innerSize}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const sprite = document.createElement('canvas');

        sprite.width = 64;
        sprite.height = 64;
        const sctx = sprite.getContext('2d');

        if (!sctx) return undefined;

        let lastSpriteColor: string | null = null;
        const buildSprite = (hex: string) => {
            const h = hex.replace('#', '');
            const tint = {
                r: parseInt(h.slice(0, 2), 16),
                g: parseInt(h.slice(2, 4), 16),
                b: parseInt(h.slice(4, 6), 16),
            };

            sctx.clearRect(0, 0, 64, 64);
            const sg = sctx.createRadialGradient(32, 32, 0, 32, 32, 32);

            sg.addColorStop(0, 'rgba(255,255,255,1)');
            sg.addColorStop(0.18, `rgba(${tint.r},${tint.g},${tint.b},0.95)`);
            sg.addColorStop(0.45, `rgba(${tint.r},${tint.g},${tint.b},0.35)`);
            sg.addColorStop(1, `rgba(${tint.r},${tint.g},${tint.b},0)`);
            sctx.fillStyle = sg;
            sctx.fillRect(0, 0, 64, 64);
            lastSpriteColor = hex;
        };

        buildSprite(orbColorRef.current);

        const radius = innerSize / 2;
        const particles = Array.from({ length: 220 }, () => {
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos(2 * Math.random() - 1);
            const u = Math.random();
            const rNorm = 0.55 + Math.sqrt(u) * 0.5;
            const speed = (0.00012 + Math.random() * 0.00026) * (Math.random() < 0.92 ? 1 : -1);

            return {
                theta,
                phi,
                rNorm,
                speed,
                baseSize: 0.5 + Math.random() * 1.7,
                twinkleSpeed: 0.0009 + Math.random() * 0.0022,
                twinklePhase: Math.random() * Math.PI * 2,
            };
        });

        const cx = innerSize / 2;
        const cy = innerSize / 2;
        let last = performance.now();
        let raf = 0;

        const tick = (now: number) => {
            if (orbColorRef.current !== lastSpriteColor) {
                buildSprite(orbColorRef.current);
            }

            const dt = Math.min(50, now - last);

            last = now;
            ctx.clearRect(0, 0, innerSize, innerSize);
            ctx.globalCompositeOperation = 'lighter';

            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];

                p.theta += p.speed * dt;
                const sinPhi = Math.sin(p.phi);
                const x3 = p.rNorm * radius * sinPhi * Math.cos(p.theta);
                const y3 = p.rNorm * radius * Math.cos(p.phi);
                const z3 = p.rNorm * radius * sinPhi * Math.sin(p.theta);
                const depth = (z3 + radius) / (2 * radius);
                const sizeMul = 0.45 + depth * 1.15;
                const alphaMul = 0.18 + depth * 0.9;
                const tw = 0.55 + 0.45 * Math.sin(now * p.twinkleSpeed + p.twinklePhase);
                const finalAlpha = Math.max(0, Math.min(1, alphaMul * tw));
                const sx = cx + x3;
                const sy = cy + y3;
                const drawSize = p.baseSize * sizeMul * 6;

                ctx.globalAlpha = finalAlpha;
                ctx.drawImage(sprite, sx - drawSize / 2, sy - drawSize / 2, drawSize, drawSize);
            }

            ctx.globalAlpha = 1;
            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);

        return () => cancelAnimationFrame(raf);
    }, [size]);

    return (
        <section
            className="lp2__hero"
            style={{
                padding: '48px 40px 64px',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '40%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 1600,
                    height: 900,
                    pointerEvents: 'none',
                    background: `radial-gradient(ellipse at 50% 50%, ${orbColor}26 0%, ${orbColor}10 30%, transparent 65%)`,
                    transition: 'background 0.6s ease',
                }}
            />

            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage:
                        'linear-gradient(rgba(232, 230, 224, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(232, 230, 224, 0.03) 1px, transparent 1px)',
                    backgroundSize: '80px 80px',
                    maskImage: 'radial-gradient(ellipse at 50% 40%, black 30%, transparent 70%)',
                    WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, black 30%, transparent 70%)',
                    pointerEvents: 'none',
                }}
            />

            <div
                style={{
                    maxWidth: 1320,
                    margin: '0 auto',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Eyebrow color={orbColor}>{t('landing.hero.eyebrow')}</Eyebrow>

                <h1
                    className="lp2__hero-title"
                    style={{
                        fontSize: 76,
                        fontWeight: 200,
                        margin: '20px 0 14px',
                        letterSpacing: '-0.045em',
                        lineHeight: 0.95,
                        color: ivory,
                        maxWidth: 1100,
                    }}
                >
                    {t('landing.hero.title.lead')}{' '}
                    <span style={{ color: muted, fontStyle: 'italic', fontFamily: serif, fontWeight: 300 }}>
                        {t('landing.hero.title.italic')}
                    </span>
                    <br />
                    <span style={{ color: orbColor, transition: 'color 0.6s ease' }}>
                        {t('landing.hero.title.accent')}
                    </span>
                </h1>

                <p
                    className="lp2__hero-subtitle"
                    style={{
                        fontSize: 17,
                        color: bodyText,
                        lineHeight: 1.5,
                        fontWeight: 300,
                        maxWidth: 680,
                        margin: '0 0 24px',
                    }}
                >
                    {t('landing.hero.subtitle')}
                </p>

                <button
                    type="button"
                    onClick={handleRestart}
                    aria-label="Replay the Development Score animation"
                    className="lp2__hero-orb"
                    style={{
                        position: 'relative',
                        width: size,
                        height: size,
                        marginBottom: 8,
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: 'inherit',
                        fontFamily: 'inherit',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            inset: 50,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${orbColor}66 0%, ${orbColor}22 35%, transparent 70%)`,
                            filter: 'blur(8px)',
                            transition: 'background 0.6s ease',
                        }}
                    />
                    <canvas
                        ref={canvasRef}
                        style={{ position: 'absolute', top: 12, left: 12, pointerEvents: 'none' }}
                    />
                    <svg width={size} height={size} style={{ position: 'absolute', inset: 0 }}>
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={r}
                            stroke="rgba(255,255,255,0.04)"
                            strokeWidth={stroke}
                            fill="none"
                        />
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={r}
                            strokeWidth={stroke}
                            fill="none"
                            strokeDasharray={c}
                            strokeDashoffset={c - (score / 100) * c}
                            strokeLinecap="round"
                            transform={`rotate(-90 ${size / 2} ${size / 2})`}
                            style={{
                                stroke: orbColor,
                                filter: `drop-shadow(0 0 6px ${orbColor})`,
                                transition: 'stroke 0.6s ease, filter 0.6s ease',
                            }}
                        />
                    </svg>
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        <div
                            style={{
                                fontSize: 10,
                                color: muted,
                                letterSpacing: '0.28em',
                                fontFamily: mono,
                                marginBottom: 4,
                            }}
                        >
                            {t('landing.hero.orb.label')}
                        </div>
                        <div
                            style={{
                                fontSize: 108,
                                fontWeight: 200,
                                color: ivory,
                                letterSpacing: '-0.05em',
                                lineHeight: 1,
                                fontFamily: mono,
                            }}
                        >
                            {scoreInt}
                        </div>
                        <div
                            style={{
                                fontSize: 11,
                                color: orbColor,
                                marginTop: 4,
                                fontFamily: mono,
                                letterSpacing: '0.1em',
                                transition: 'color 0.6s ease',
                            }}
                        >
                            {zoneName}
                        </div>
                    </div>
                </button>

                {/* Journey caption - swaps as the orb climbs through the tiers */}
                <div
                    aria-live="polite"
                    style={{
                        minHeight: 24,
                        marginBottom: 18,
                        fontSize: 14,
                        fontFamily: serif,
                        fontStyle: 'italic',
                        color: orbColor,
                        letterSpacing: '-0.01em',
                        transition: 'color 0.6s ease',
                    }}
                    key={caption}
                    className="lp2__hero-caption"
                >
                    {caption}
                </div>

                <div
                    className="lp2__hero-cta"
                    style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 0 }}
                >
                    <button
                        type="button"
                        onClick={() => navigate(QUIZ_PATH)}
                        className={isPeak ? 'lp2__cta-primary lp2__cta-primary--peak' : 'lp2__cta-primary'}
                        style={{
                            background: '#ef4444',
                            color: ivory,
                            border: 'none',
                            padding: '14px 30px',
                            borderRadius: 4,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontFamily: mono,
                            letterSpacing: '0.1em',
                            boxShadow: isPeak
                                ? '0 0 0 2px rgba(239, 68, 68, 0.6), 0 0 60px rgba(239, 68, 68, 0.7)'
                                : '0 0 32px rgba(239, 68, 68, 0.4)',
                            transform: isPeak ? 'scale(1.04)' : 'scale(1)',
                            transition: 'box-shadow 0.4s ease, transform 0.4s ease',
                        }}
                    >
                        {t('landing.hero.cta.start')}
                    </button>
                    <a
                        href="#dimensions"
                        style={{
                            background: 'transparent',
                            color: ivory,
                            border: hairline,
                            padding: '13px 24px',
                            borderRadius: 4,
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: 'pointer',
                            fontFamily: mono,
                            letterSpacing: '0.1em',
                            textDecoration: 'none',
                        }}
                    >
                        {t('landing.hero.cta.sample')}
                    </a>
                </div>
                <div
                    style={{
                        marginTop: 12,
                        fontSize: 11,
                        color: subtle,
                        fontFamily: mono,
                        letterSpacing: '0.18em',
                    }}
                >
                    {t('landing.hero.meta')}
                </div>
            </div>
        </section>
    );
};

const SpecStrip: React.FC = () => {
    const t = useT();
    const specs: { id: string; n: string; label: string }[] = [
        { id: 'questions', n: t('landing.specs.questions.value'), label: t('landing.specs.questions.label') },
        { id: 'duration', n: t('landing.specs.duration.value'), label: t('landing.specs.duration.label') },
        { id: 'dimensions', n: t('landing.specs.dimensions.value'), label: t('landing.specs.dimensions.label') },
        { id: 'patterns', n: t('landing.specs.patterns.value'), label: t('landing.specs.patterns.label') },
    ];

    return (
        <section style={{ padding: '32px 40px', borderTop: hairline, borderBottom: hairline }}>
            <div
                className="lp2__specs"
                style={{
                    maxWidth: 1320,
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 1,
                    background: 'rgba(232,230,224,0.06)',
                }}
            >
                {specs.map(({ id, n, label }) => (
                    <div key={id} style={{ background: '#0a0a0a', padding: '24px 28px' }}>
                        <div
                            style={{
                                fontSize: 36,
                                fontWeight: 300,
                                color: ivory,
                                letterSpacing: '-0.03em',
                                fontFamily: mono,
                                lineHeight: 1,
                            }}
                        >
                            {n}
                        </div>
                        <div
                            style={{
                                fontSize: 11,
                                color: muted,
                                letterSpacing: '0.18em',
                                fontFamily: mono,
                                marginTop: 6,
                                textTransform: 'uppercase',
                            }}
                        >
                            {label}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

const Findings: React.FC = () => {
    const t = useT();
    const claims = [
        {
            n: '01',
            big: t('landing.findings.01.big'),
            sub: t('landing.findings.01.sub'),
            body: t('landing.findings.01.body'),
            cite: t('landing.findings.01.cite'),
            color: ACCENT.gold,
        },
        {
            n: '02',
            big: t('landing.findings.02.big'),
            sub: t('landing.findings.02.sub'),
            body: t('landing.findings.02.body'),
            cite: t('landing.findings.02.cite'),
            color: ACCENT.ember,
        },
    ];

    return (
        <section style={{ padding: '120px 40px', borderTop: hairline }}>
            <div style={{ maxWidth: 1320, margin: '0 auto' }}>
                <div style={{ marginBottom: 64, maxWidth: 720 }}>
                    <Eyebrow>{t('landing.findings.eyebrow')}</Eyebrow>
                    <h2
                        className="lp2__h2"
                        style={{
                            fontSize: 56,
                            fontWeight: 300,
                            margin: '14px 0 0',
                            letterSpacing: '-0.03em',
                            lineHeight: 1.05,
                            color: ivory,
                        }}
                    >
                        {t('landing.findings.title.lead')}{' '}
                        <span style={{ color: muted, fontStyle: 'italic', fontFamily: serif, fontWeight: 300 }}>
                            {t('landing.findings.title.italic')}
                        </span>
                    </h2>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 0, borderTop: hairline }}>
                    {claims.map((claim) => (
                        <div
                            key={claim.n}
                            className="lp2__finding"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '120px 1.4fr 1fr',
                                gap: 48,
                                padding: '56px 0',
                                borderBottom: hairline,
                                alignItems: 'flex-start',
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: claim.color,
                                        letterSpacing: '0.22em',
                                        fontFamily: mono,
                                        marginBottom: 12,
                                    }}
                                >
                                    {t('landing.findings.label')} {claim.n}
                                </div>
                                <div
                                    style={{
                                        width: 56,
                                        height: 2,
                                        background: claim.color,
                                        boxShadow: `0 0 8px ${claim.color}`,
                                    }}
                                />
                            </div>
                            <div>
                                <h3
                                    className="lp2__finding-title"
                                    style={{
                                        fontSize: 38,
                                        fontWeight: 300,
                                        margin: '0 0 16px',
                                        letterSpacing: '-0.025em',
                                        lineHeight: 1.15,
                                        color: ivory,
                                    }}
                                >
                                    {claim.big}
                                </h3>
                                <p
                                    style={{
                                        fontSize: 16,
                                        color: muted,
                                        fontStyle: 'italic',
                                        margin: 0,
                                        fontFamily: serif,
                                    }}
                                >
                                    {claim.sub}
                                </p>
                            </div>
                            <div>
                                <p style={{ fontSize: 15, color: bodyText, lineHeight: 1.7, margin: '0 0 18px' }}>
                                    {claim.body}
                                </p>
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: subtle,
                                        fontFamily: mono,
                                        letterSpacing: '0.08em',
                                    }}
                                >
                                    {claim.cite}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const Process: React.FC = () => {
    const t = useT();
    const steps = [
        {
            n: '01',
            meta: t('landing.process.01.meta'),
            title: t('landing.process.01.title'),
            body: t('landing.process.01.body'),
        },
        {
            n: '02',
            meta: t('landing.process.02.meta'),
            title: t('landing.process.02.title'),
            body: t('landing.process.02.body'),
        },
        {
            n: '03',
            meta: t('landing.process.03.meta'),
            title: t('landing.process.03.title'),
            body: t('landing.process.03.body'),
        },
    ];

    return (
        <section
            id="method"
            style={{
                padding: '120px 40px',
                borderTop: hairline,
                background: 'rgba(245, 158, 11, 0.015)',
            }}
        >
            <div style={{ maxWidth: 1320, margin: '0 auto' }}>
                <div
                    className="lp2__section-head"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        marginBottom: 56,
                        gap: 32,
                        flexWrap: 'wrap',
                    }}
                >
                    <div>
                        <Eyebrow>{t('landing.process.eyebrow')}</Eyebrow>
                        <h2
                            className="lp2__h2"
                            style={{
                                fontSize: 56,
                                fontWeight: 300,
                                margin: '14px 0 0',
                                letterSpacing: '-0.03em',
                                lineHeight: 1.05,
                                color: ivory,
                                maxWidth: 720,
                            }}
                        >
                            {t('landing.process.title.lead')}{' '}
                            <span style={{ color: muted }}>{t('landing.process.title.muted')}</span>
                        </h2>
                    </div>
                </div>

                <div
                    className="lp2__process"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}
                >
                    {steps.map((s) => (
                        <div
                            key={s.n}
                            style={{
                                background: '#0c0b0a',
                                border: hairline,
                                borderRadius: 4,
                                padding: '40px 36px 36px',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: 2,
                                    width: '32%',
                                    background: ACCENT.gold,
                                    boxShadow: `0 0 8px ${ACCENT.gold}`,
                                }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'baseline',
                                    marginBottom: 32,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 56,
                                        fontWeight: 200,
                                        color: '#1a1816',
                                        letterSpacing: '-0.04em',
                                        lineHeight: 1,
                                        fontFamily: mono,
                                    }}
                                >
                                    {s.n}
                                </div>
                                <div
                                    style={{
                                        fontSize: 10,
                                        color: muted,
                                        letterSpacing: '0.22em',
                                        fontFamily: mono,
                                    }}
                                >
                                    {s.meta}
                                </div>
                            </div>
                            <h3
                                style={{
                                    fontSize: 22,
                                    fontWeight: 500,
                                    margin: '0 0 14px',
                                    letterSpacing: '-0.015em',
                                    color: ivory,
                                    lineHeight: 1.25,
                                }}
                            >
                                {s.title}
                            </h3>
                            <p style={{ fontSize: 14, color: bodyText, lineHeight: 1.65, margin: 0 }}>{s.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// Lifted from src/components/dashboard/editorial/DimensionCard.tsx - same look:
// thick dark track + thin colored arc + soft inner glow, centered % readout.
const DimensionRing: React.FC<{ percent: number; color: string; size?: number }> = ({
    percent,
    color,
    size = 156,
}) => {
    const stroke = 12;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (percent / 100) * c;

    return (
        <div
            style={{
                position: 'relative',
                width: size,
                height: size,
                margin: '0 auto',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    inset: stroke + 2,
                    borderRadius: '50%',
                    background: `radial-gradient(circle at 50% 50%, ${color}3d 0%, ${color}1a 38%, transparent 72%)`,
                    pointerEvents: 'none',
                }}
            />
            <svg
                width={size}
                height={size}
                style={{ transform: 'rotate(-90deg)', position: 'relative' }}
            >
                <circle cx={size / 2} cy={size / 2} r={r} stroke="#1a1816" strokeWidth={stroke} fill="none" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    stroke={color}
                    strokeWidth={stroke - 6}
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={c}
                    strokeDashoffset={offset}
                    style={{ filter: `drop-shadow(0 0 4px ${color}aa)` }}
                />
            </svg>
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <div
                    style={{
                        fontSize: 32,
                        fontWeight: 200,
                        color: ivory,
                        letterSpacing: '-0.04em',
                        lineHeight: 1,
                        fontFamily: mono,
                    }}
                >
                    {percent}
                    <span style={{ fontSize: 14, color: muted, fontWeight: 400 }}>%</span>
                </div>
            </div>
        </div>
    );
};

/**
 * Sample scores for the eight rings — an illustrative profile, not anyone's
 * data. Keyed by track so the numbers stay attached to their card if the order
 * ever changes, and spread across the scale so all four zones are represented.
 */
const SAMPLE_SCORE: Record<DimensionId, number> = {
    cognition: 42,
    affect: 58,
    attention: 47,
    self: 64,
    motivation: 71,
    behavior: 55,
    biophysiology: 76,
    social: 81,
};

const Dimensions: React.FC = () => {
    const t = useT();
    // The eight tracks in the product's own order, from the shared metadata —
    // the landing used to carry its own list of five, which drifted the moment
    // the model moved to eight.
    const dims = DIMENSION_ORDER.map((id) => ({
        id,
        label: t(`landing.directions.${id}.label`),
        pct: SAMPLE_SCORE[id],
        color: DIMENSION_COLORS[id],
        title: t(`landing.directions.${id}.title`),
        desc: t(`landing.directions.${id}.desc`),
    }));

    return (
        <section id="dimensions" style={{ padding: '120px 40px', borderTop: hairline }}>
            <div style={{ maxWidth: 1320, margin: '0 auto' }}>
                <div style={{ marginBottom: 56, maxWidth: 720 }}>
                    <Eyebrow>{t('landing.dimensions.eyebrow')}</Eyebrow>
                    <h2
                        className="lp2__h2"
                        style={{
                            fontSize: 56,
                            fontWeight: 300,
                            margin: '14px 0 0',
                            letterSpacing: '-0.03em',
                            lineHeight: 1.05,
                            color: ivory,
                        }}
                    >
                        {t('landing.dimensions.title.lead')}{' '}
                        <span style={{ color: muted, fontStyle: 'italic', fontFamily: serif, fontWeight: 300 }}>
                            {t('landing.dimensions.title.italic')}
                        </span>
                    </h2>
                </div>

                <div
                    className="lp2__dim-grid"
                    style={{
                        display: 'grid',
                        // Four across, two rows: five columns would leave the
                        // eighth card orphaned on a row of its own.
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 1,
                        background: 'rgba(232,230,224,0.06)',
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: hairline,
                    }}
                >
                    {dims.map((d) => (
                        <div
                            key={d.id}
                            style={{
                                background: '#0a0908',
                                padding: '32px 24px 28px',
                                position: 'relative',
                                minHeight: 380,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 11,
                                    color: d.color,
                                    letterSpacing: '0.2em',
                                    fontFamily: mono,
                                    fontWeight: 600,
                                    textAlign: 'center',
                                }}
                            >
                                {d.label}
                            </div>

                            <DimensionRing percent={d.pct} color={d.color} />

                            <div style={{ textAlign: 'center' }}>
                                <h3
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 500,
                                        color: ivory,
                                        margin: '0 0 6px',
                                        letterSpacing: '-0.005em',
                                        lineHeight: 1.25,
                                        minHeight: '2.5em',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {d.title}
                                </h3>
                                <p
                                    style={{
                                        fontSize: 12,
                                        color: muted,
                                        lineHeight: 1.55,
                                        margin: 0,
                                        minHeight: '3.1em',
                                    }}
                                >
                                    {d.desc}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const Quote: React.FC = () => {
    const t = useT();

    return (
        <section
            style={{
                padding: '140px 40px',
                borderTop: hairline,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 1200,
                    height: 600,
                    pointerEvents: 'none',
                    background: `radial-gradient(ellipse at 50% 50%, ${ACCENT.ember}1a 0%, transparent 60%)`,
                }}
            />
            <div style={{ maxWidth: 1080, margin: '0 auto', position: 'relative', textAlign: 'center' }}>
                <div
                    style={{
                        fontSize: 56,
                        color: ACCENT.gold,
                        fontFamily: serif,
                        lineHeight: 1,
                        marginBottom: 16,
                    }}
                >
                    “
                </div>
                <p
                    className="lp2__quote-text"
                    style={{
                        fontSize: 44,
                        fontWeight: 300,
                        lineHeight: 1.2,
                        letterSpacing: '-0.025em',
                        margin: '0 0 32px',
                        color: ivory,
                        fontFamily: serif,
                        fontStyle: 'italic',
                    }}
                >
                    {t('landing.quote.lead')} <br />
                    <span style={{ color: muted }}>{t('landing.quote.muted')}</span>
                </p>
                <div
                    style={{
                        fontSize: 11,
                        color: subtle,
                        fontFamily: mono,
                        letterSpacing: '0.18em',
                    }}
                >
                    {t('landing.quote.cite')}
                </div>
            </div>
        </section>
    );
};

const ScienceBlock: React.FC = () => {
    const t = useT();
    const items: { title: string; body: string; badge?: string }[] = [
        { title: t('landing.science.01.title'), body: t('landing.science.01.body') },
        { title: t('landing.science.02.title'), body: t('landing.science.02.body') },
        { title: t('landing.science.03.title'), body: t('landing.science.03.body') },
        { title: t('landing.science.04.title'), body: t('landing.science.04.body'), badge: t('landing.science.badge.soon') },
        { title: t('landing.science.05.title'), body: t('landing.science.05.body'), badge: t('landing.science.badge.soon') },
    ];
    const researchers = ['Kahneman', 'Kegan', 'Baumeister', 'Fredrickson', 'Achor'];

    return (
        <section
            id="research"
            style={{ padding: '120px 40px', borderTop: hairline, background: '#08070790' }}
        >
            <div style={{ maxWidth: 1080, margin: '0 auto' }}>
                <Eyebrow color={ACCENT.gold}>{t('landing.science.eyebrow')}</Eyebrow>
                <h2
                    className="lp2__h2"
                    style={{
                        fontSize: 56,
                        fontWeight: 300,
                        margin: '14px 0 24px',
                        letterSpacing: '-0.03em',
                        lineHeight: 1.05,
                        color: ivory,
                    }}
                >
                    {t('landing.science.title.lead')}{' '}
                    <span
                        style={{
                            fontStyle: 'italic',
                            fontFamily: serif,
                            color: ACCENT.gold,
                            fontWeight: 300,
                        }}
                    >
                        {t('landing.science.title.italic')}
                    </span>{' '}
                    {t('landing.science.title.tail')}{' '}
                    <span style={{ color: muted }}>{t('landing.science.title.muted')}</span>
                </h2>
                <p
                    style={{
                        fontSize: 17,
                        color: bodyText,
                        lineHeight: 1.65,
                        margin: '0 0 48px',
                        maxWidth: 820,
                    }}
                >
                    {t('landing.science.body')}
                </p>

                <div
                    className="lp2__researchers"
                    style={{
                        display: 'flex',
                        gap: 28,
                        flexWrap: 'wrap',
                        padding: '20px 0 32px',
                        marginBottom: 32,
                        borderTop: hairline,
                        borderBottom: hairline,
                    }}
                >
                    <div
                        style={{
                            fontSize: 11,
                            color: muted,
                            letterSpacing: '0.22em',
                            alignSelf: 'center',
                            fontFamily: mono,
                        }}
                    >
                        {t('landing.science.builton')}
                    </div>
                    {researchers.map((r) => (
                        <div
                            key={r}
                            style={{
                                fontSize: 16,
                                color: ivory,
                                fontFamily: serif,
                                fontStyle: 'italic',
                            }}
                        >
                            {r}
                        </div>
                    ))}
                </div>

                {items.map(({ title, body, badge }, i) => (
                    <div
                        key={title}
                        className="lp2__science-row"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '40px 1fr auto',
                            gap: 24,
                            alignItems: 'flex-start',
                            padding: '24px 0',
                            borderBottom: i < items.length - 1 ? hair2 : 'none',
                        }}
                    >
                        <div
                            style={{
                                fontSize: 12,
                                color: subtle,
                                fontFamily: mono,
                                letterSpacing: '0.05em',
                                paddingTop: 5,
                            }}
                        >
                            {String(i + 1).padStart(2, '0')}
                        </div>
                        <div>
                            <div
                                style={{
                                    fontSize: 19,
                                    fontWeight: 500,
                                    color: ivory,
                                    marginBottom: 6,
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                {title}
                            </div>
                            <div style={{ fontSize: 14, color: bodyText, lineHeight: 1.6 }}>{body}</div>
                        </div>
                        {badge && (
                            <span
                                style={{
                                    fontSize: 9,
                                    color: muted,
                                    padding: '3px 8px',
                                    border: hair2,
                                    borderRadius: 999,
                                    fontFamily: mono,
                                    letterSpacing: '0.1em',
                                }}
                            >
                                {badge}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
};

const FinalCTA: React.FC = () => {
    const navigate = useNavigate();
    const t = useT();

    return (
        <section
            style={{
                padding: '160px 40px',
                borderTop: hairline,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 1400,
                    height: 800,
                    pointerEvents: 'none',
                    background: 'radial-gradient(ellipse at 50% 50%, #ef444422 0%, transparent 60%)',
                }}
            />
            <div
                style={{
                    maxWidth: 920,
                    margin: '0 auto',
                    textAlign: 'center',
                    position: 'relative',
                }}
            >
                <Eyebrow color="#ef4444">{t('landing.finalcta.eyebrow')}</Eyebrow>
                <h2
                    className="lp2__cta-title"
                    style={{
                        fontSize: 88,
                        fontWeight: 200,
                        margin: '20px 0 28px',
                        letterSpacing: '-0.045em',
                        lineHeight: 0.98,
                        color: ivory,
                    }}
                >
                    {t('landing.finalcta.title.lead')}{' '}
                    <span
                        style={{
                            fontStyle: 'italic',
                            fontFamily: serif,
                            color: '#ef4444',
                            fontWeight: 300,
                        }}
                    >
                        {t('landing.finalcta.title.italic')}
                    </span>
                </h2>
                <p
                    style={{
                        fontSize: 18,
                        color: bodyText,
                        lineHeight: 1.6,
                        margin: '0 auto 44px',
                        maxWidth: 560,
                    }}
                >
                    {t('landing.finalcta.body')}
                </p>
                <button
                    type="button"
                    onClick={() => navigate(QUIZ_PATH)}
                    style={{
                        background: '#ef4444',
                        color: ivory,
                        border: 'none',
                        padding: '20px 44px',
                        borderRadius: 4,
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: mono,
                        letterSpacing: '0.12em',
                        boxShadow: '0 0 40px rgba(239, 68, 68, 0.45)',
                    }}
                >
                    {t('landing.finalcta.button')}
                </button>
                <div
                    style={{
                        marginTop: 22,
                        fontSize: 11,
                        color: subtle,
                        fontFamily: mono,
                        letterSpacing: '0.18em',
                    }}
                >
                    {t('landing.finalcta.meta')}
                </div>
            </div>
        </section>
    );
};

const HomePage: React.FC = () => (
    <div
        className="lp2"
        style={{
            background: '#0a0a0a',
            color: ivory,
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif",
            minHeight: '100vh',
        }}
    >
        <EditorialNav />
        <Hero />
        <SpecStrip />
        <Findings />
        <Process />
        <Dimensions />
        <Quote />
        <ScienceBlock />
        <FinalCTA />
        <EditorialFooter />
    </div>
);

export default HomePage;
